import { damageZombie } from './damage/damageHandler.js';
import * as THREE from 'three';
// Import pistol skill for XP rewards
import { pistolSkill } from '../SkillAdapter.js';
// Import the ZombieModel from the local directory
import { ZombieModel } from '../models/ZombieModel.js';
// Import the BossZombieModel for boss zombies
import { BossZombieModel } from '../models/BossZombieModel.js';
// Import the RunnerZombieModel for fast zombies
import { RunnerZombieModel } from '../models/RunnerZombieModel.js';
// Import mobile detection
import { detectPlatform } from '../settings/PlatformSettings.js';

const DEBUG = true; // Add DEBUG constant

export class ZombieManager {
    constructor(scene) {
        this.scene = scene;
        this.zombies = [];
        this.maxZombies = 10; // Add missing property
        
        // Zombie health properties
        this.startingHealth = 75; // Reduced from 100 for easier kills
        this.runnerZombieHealth = 100; // Runner zombies have a bit more health than regular ones
        this.bossZombieHealth = 250; // Boss zombies have more health
        
        // Zombie damage properties
        this.zombieDamage = {
            regular: 10,      // Regular zombies deal 10 damage per hit
            runner: 8,        // Runner zombies deal less damage but attack faster
            boss: 25          // Boss zombies deal significant damage
        };
        
        // Zombie attack cooldowns (in milliseconds)
        this.zombieAttackCooldown = {
            regular: 1500,    // Regular zombies attack every 1.5 seconds
            runner: 1000,     // Runner zombies attack faster (every 1 second)
            boss: 2000        // Boss zombies attack slower but hit harder
        };
        
        // Attack range for each zombie type
        this.zombieAttackRange = {
            regular: 1.8,     // Regular zombies have standard range
            runner: 2.0,      // Runner zombies have slightly longer reach
            boss: 2.2         // Boss zombies have the longest reach
        };
        
        // Set hitbox size based on platform
        const platform = detectPlatform();
        if (platform === 'mobile') {
            // Larger hitbox for mobile devices
            this.hitboxSize = new THREE.Vector3(4, 5, 4); // Increased from 3,4,3
        } else {
            // Standard hitbox for desktop
            this.hitboxSize = new THREE.Vector3(3, 4, 3);
        }
        
        window.zombieManager = this; // Make it globally accessible
        this.zombieColor = 0x006400;  // Default green color
        
        // Track spawns to create boss zombies
        this.zombiesSpawned = 0;

        // Reference the damage container from the HTML
        this.damageContainer = document.getElementById('damageContainer');
        if (!this.damageContainer) {
            console.error('Damage container not found in HTML!');
        } else {
            console.log('Damage container successfully found:', this.damageContainer);
        }
        
        // Wave system properties
        this.waveInProgress = false;
        this.currentWaveZombies = {
            base: 0,
            runners: 0,
            bosses: 0
        };
        this.spawnInterval = null;
        this.spawnDelay = 1000; // ms between zombie spawns within a wave
    }

    spawnZombie(playerCube) {
        if (this.zombies.length >= this.maxZombies) return;

        // Increment spawn counter
        this.zombiesSpawned++;

        // Generate spawn position with proper height check
        const angle = Math.random() * Math.PI * 2;
        const distance = 15;
        const spawnX = playerCube.position.x + Math.cos(angle) * distance;
        const spawnZ = playerCube.position.z + Math.sin(angle) * distance;
        const groundHeight = window.chunkManager.getHeightAt(spawnX, spawnZ);
        const spawnY = groundHeight + 1;

        // Determine zombie type to spawn
        // Boss zombie every 5th spawn
        const isBossZombie = this.zombiesSpawned % 5 === 0;
        // Runner zombie every 3rd spawn (but not if it's a boss spawn)
        const isRunnerZombie = !isBossZombie && this.zombiesSpawned % 3 === 0;
        
        // Create appropriate zombie type
        let zombie;
        
        if (isBossZombie) {
            zombie = this.createBossZombie(spawnX, spawnY, spawnZ);
        } else if (isRunnerZombie) {
            zombie = this.createRunnerZombie(spawnX, spawnY, spawnZ);
        } else {
            zombie = this.createRegularZombie(spawnX, spawnY, spawnZ);
        }
        
        // Add to tracking arrays
        this.zombies.push(zombie);
        this.scene.add(zombie);
        
        if (DEBUG) {
            let zombieType = "zombie";
            if (isBossZombie) zombieType = "BOSS zombie";
            if (isRunnerZombie) zombieType = "RUNNER zombie";
            console.log(`${zombieType} spawned at: ${spawnX.toFixed(2)}, ${spawnY.toFixed(2)}, ${spawnZ.toFixed(2)}`);
        }
    }
    
    /**
     * Create a regular zombie at the specified position
     */
    createRegularZombie(x, y, z) {
        // Create zombie model
        const zombieModel = new ZombieModel();
        zombieModel.position.set(x, y, z);
        
        // Enable shadows on all parts of the zombie
        zombieModel.enableShadows();
        
        // Store original color reference for damage flashing
        zombieModel.originalColor = this.zombieColor;
        
        // Create hitbox for the zombie
        zombieModel.hitbox = new THREE.Box3();
        zombieModel.health = this.startingHealth;
        
        // Add tracking for weapon that last hit this zombie
        zombieModel.lastDamagedBy = null;
        zombieModel.lastDamagedTime = 0;
        zombieModel.isBoss = false;
        zombieModel.isRunner = false;
        
        // Combat properties for attacking player
        zombieModel.damage = this.zombieDamage.regular;
        zombieModel.attackCooldown = this.zombieAttackCooldown.regular;
        zombieModel.attackRange = this.zombieAttackRange.regular;
        zombieModel.lastAttackTime = 0;
        zombieModel.type = 'regular';
        
        return zombieModel;
    }
    
    /**
     * Create a runner zombie at the specified position
     */
    createRunnerZombie(x, y, z) {
        // Create runner zombie model
        const runnerZombie = new RunnerZombieModel();
        runnerZombie.position.set(x, y, z);
        
        // Enable shadows on all parts of the zombie
        runnerZombie.enableShadows();
        
        // Store original color reference for damage flashing
        runnerZombie.originalColor = this.zombieColor;
        
        // Create hitbox for the zombie
        runnerZombie.hitbox = new THREE.Box3();
        runnerZombie.health = this.runnerZombieHealth;
        
        // Add tracking for weapon that last hit this zombie
        runnerZombie.lastDamagedBy = null;
        runnerZombie.lastDamagedTime = 0;
        runnerZombie.isBoss = false;
        runnerZombie.isRunner = true;
        
        // Runner zombies have a chance to do a leap attack
        runnerZombie.leapAttackProbability = 0.3;
        
        // Combat properties for attacking player
        runnerZombie.damage = this.zombieDamage.runner;
        runnerZombie.attackCooldown = this.zombieAttackCooldown.runner;
        runnerZombie.attackRange = this.zombieAttackRange.runner;
        runnerZombie.lastAttackTime = 0;
        runnerZombie.type = 'runner';
        
        return runnerZombie;
    }
    
    /**
     * Create a boss zombie at the specified position
     */
    createBossZombie(x, y, z) {
        // Create boss zombie model
        const bossZombie = new BossZombieModel();
        bossZombie.position.set(x, y, z);
        
        // Enable shadows on all parts of the zombie
        bossZombie.enableShadows();
        
        // Store original color reference for damage flashing
        bossZombie.originalColor = this.zombieColor;
        
        // Create hitbox for the zombie - larger for boss
        bossZombie.hitbox = new THREE.Box3();
        bossZombie.health = this.bossZombieHealth;
        
        // Add tracking for weapon that last hit this zombie
        bossZombie.lastDamagedBy = null;
        bossZombie.lastDamagedTime = 0;
        bossZombie.isBoss = true;
        bossZombie.isRunner = false;
        
        // 20% chance to do special attack when close to player
        bossZombie.specialAttackProbability = 0.2;
        
        // Combat properties for attacking player
        bossZombie.damage = this.zombieDamage.boss;
        bossZombie.attackCooldown = this.zombieAttackCooldown.boss;
        bossZombie.attackRange = this.zombieAttackRange.boss;
        bossZombie.lastAttackTime = 0;
        bossZombie.type = 'boss';
        
        return bossZombie;
    }

    checkProjectileCollision(projectile) {
        for (const zombie of this.zombies) {
            // Use mesh position for checking collision
            const distance = projectile.position.distanceTo(zombie.position);
            if (distance < 3) { // Increased collision radius from 2 to 3
                console.log(`Collision detected with zombie at ${zombie.position.x.toFixed(2)}, ${zombie.position.y.toFixed(2)}, ${zombie.position.z.toFixed(2)}`);
                return zombie;
            }
        }
        return null;
    }

    // OLD damageZombie function replaced by module import

    createDamageNumber(position, damage) {
        // Convert world position to screen position
        const tempV = new THREE.Vector3();
        tempV.copy(position);
        tempV.project(window.camera);
        
        const x = (tempV.x + 1) * window.innerWidth / 2;
        const y = (-tempV.y + 1) * window.innerHeight / 2;

        // Create damage number element
        const damageText = document.createElement('div');
        damageText.textContent = `-${damage}`;
        damageText.style.cssText = `
            position: fixed;
            left: ${x}px;
            top: ${y}px;
            color: #ff0000;
            font-size: 24px; // Reduced from 32px
            font-weight: bold;
            font-family: Arial;
            text-shadow: 2px 2px 0 #000;
            pointer-events: none;
            user-select: none;
            z-index: 10000;
            transform: translate(-50%, -50%);
        `;

        document.body.appendChild(damageText);

        // Animate
        let startTime = performance.now();
        let duration = 1000;

        function animate() {
            const elapsed = performance.now() - startTime;
            const progress = elapsed / duration;

            if (progress >= 1) {
                damageText.remove();
                return;
            }

            damageText.style.transform = `translate(-50%, ${-50 - progress * 50}px)`;
            damageText.style.opacity = 1 - progress;

            requestAnimationFrame(animate);
        }

        requestAnimationFrame(animate);
    }

    damageZombie(zombie, amount, sourceWeapon) {
        // Adjust damage for boss zombies (they take less damage)
        const actualDamage = zombie.isBoss ? Math.ceil(amount * 0.7) : amount;
        
        console.log(`${zombie.isBoss ? 'BOSS' : 'Zombie'} hit with`, actualDamage, "damage from", sourceWeapon ? sourceWeapon.name : "unknown source");
        
        // Track the weapon that hit this zombie
        if (sourceWeapon) {
            zombie.lastDamagedBy = sourceWeapon;
            zombie.lastDamagedTime = performance.now();
        }
        
        // Existing damage logic
        zombie.health -= actualDamage;
        
        // Create a flash effect for damage
        const flashDuration = 100; // Duration of the flash in ms
        const flashIntensity = 2.0; // How bright the flash should be
        
        // Store original materials if not already stored
        zombie.traverse(child => {
            if (child instanceof THREE.Mesh && child.material) {
                if (!child.originalMaterial) {
                    child.originalMaterial = child.material.clone();
                }
                
                // Create a new material for the flash effect
                const flashMaterial = new THREE.MeshStandardMaterial({
                    color: 0xff0000,
                    emissive: 0xff0000,
                    emissiveIntensity: flashIntensity,
                    metalness: child.originalMaterial.metalness,
                    roughness: child.originalMaterial.roughness
                });
                
                // Apply flash material
                child.material = flashMaterial;
                
                // Reset to original material after flash duration
                setTimeout(() => {
                    if (child.material === flashMaterial) {
                        child.material = child.originalMaterial;
                    }
                }, flashDuration);
            }
        });
        
        // Check if zombie is dead
        if (zombie.health <= 0) {
            this.killZombie(zombie);
        }
        
        // Create floating damage number
        this.createDamageNumber(zombie.position.clone(), actualDamage);
    }

    /**
     * Kill a zombie with death animation
     * @param {Object} zombie - The zombie to kill
     * @param {boolean} isWaveCompletion - Whether this kill is from wave completion
     */
    killZombie(zombie, isWaveCompletion = false) {
        // Only award XP for player weapon kills that aren't from wave completion
        if (!isWaveCompletion && zombie.lastDamagedBy && zombie.lastDamagedBy.name) {
            if (zombie.isBoss) {
                pistolSkill.addXP(50); // More XP for boss kills
            } else if (zombie.isRunner) {
                pistolSkill.addXP(15); // More XP for runner kills
            } else {
                pistolSkill.addXP(10); // Base XP for regular kills
            }
        }
        
        // Start death animation
        const startTime = performance.now();
        const deathDuration = 1000; // 1 second death animation
        
        const animate = () => {
            const elapsed = performance.now() - startTime;
            const progress = elapsed / deathDuration;
            
            if (progress < 1) {
                // Fade out and sink into ground
                zombie.position.y -= 0.1;
                
                // Fade out all materials
                zombie.traverse(child => {
                    if (child instanceof THREE.Mesh && child.material) {
                        if (child.material.opacity !== undefined) {
                            child.material.opacity = 1 - progress;
                        }
                    }
                });
                
                // Continue animation
                requestAnimationFrame(animate);
            } else {
                // Clean up zombie
                const index = this.zombies.indexOf(zombie);
                if (index > -1) {
                    this.zombies.splice(index, 1);
                    this.scene.remove(zombie);
                    
                    // Notify the wave manager that a zombie was killed
                    if (window.waveManager) {
                        window.waveManager.zombieKilled();
                    }
                    
                    // Only spawn drops for non-wave-completion kills
                    if (!isWaveCompletion && window.powerUpDropManager) {
                        try {
                            window.powerUpDropManager.maybeDrop(zombie.type, window.waveManager?.getWave() || 1, zombie.position);
                        } catch (err) {
                            console.error('[ZombieManager] Error spawning drop:', err);
                        }
                    }
                }
            }
        };
        
        requestAnimationFrame(animate);
    }

    updateZombies(playerCube) {
        // Get reference to player controller
        const playerController = window.playerController;
        
        // Get delta time from game loop
        const deltaTime = window.clock?.getDelta() || 1/60; // Fallback to 60fps if clock not available
        
        this.zombies.forEach(zombie => {
            // Calculate new position
            const dirX = playerCube.position.x - zombie.position.x;
            const dirZ = playerCube.position.z - zombie.position.z;
            const distanceToPlayer = Math.sqrt(dirX * dirX + dirZ * dirZ);
            
            // Different movement speeds based on zombie type
            let speed = this.zombieSpeed.regular; // Regular zombie speed
            
            if (zombie.isBoss) {
                speed = this.zombieSpeed.boss; // Boss zombies move slower
            } else if (zombie.isRunner) {
                speed = this.zombieSpeed.runner; // Runner zombies move faster
            }
            
            // Check if zombie is close enough to attack
            const canAttack = performance.now() - zombie.lastAttackTime > zombie.attackCooldown;
            const isInAttackRange = distanceToPlayer < zombie.attackRange;
            
            if (isInAttackRange) {
                // Update the last attack time
                zombie.lastAttackTime = performance.now();
                
                // Trigger appropriate attack animation
                if (zombie.isBoss && zombie.startSpecialAttack && Math.random() < zombie.specialAttackProbability) {
                    // Boss zombies have a chance to do a special attack
                    zombie.startSpecialAttack();
                } else if (zombie.isRunner && zombie.startLeapAttack && Math.random() < zombie.leapAttackProbability) {
                    // Runner zombies have a chance to do a leap attack
                    zombie.startLeapAttack();
                } else if (zombie.startAttack) {
                    // Regular attack for any zombie type
                    zombie.startAttack();
                }
                
                // Log attack attempt for debugging
                if (DEBUG) {
                    let zombieType = zombie.type || "regular";
                    console.log(`${zombieType} zombie attacking player. Damage: ${zombie.damage}`);
                }
                
                // Apply damage to player if playerController exists
                if (playerController && typeof playerController.takeDamage === 'function') {
                    // Special damage handling for leap attacks (more damage)
                    let damageAmount = zombie.damage;
                    
                    // Special case for runner's leap attack (more damage)
                    if (zombie.isRunner && zombie.animationState && zombie.animationState.leapAttack) {
                        damageAmount = Math.ceil(damageAmount * 1.5); // 50% more damage for leap attacks
                    }
                    
                    // Special case for boss's special attack (more damage)
                    if (zombie.isBoss && zombie.animationState && zombie.animationState.specialAttack) {
                        damageAmount = Math.ceil(damageAmount * 1.75); // 75% more damage for special attacks
                    }
                    
                    // Apply damage to player with source
                    playerController.takeDamage(damageAmount, `${zombie.type} zombie`);
                }
            }
            
            // Calculate normalized direction
            const length = Math.sqrt(dirX * dirX + dirZ * dirZ);
            const normDirX = dirX / length;
            const normDirZ = dirZ / length;
            
            // Calculate new position with deltaTime-based movement
            const newX = zombie.position.x + normDirX * speed * deltaTime;
            const newZ = zombie.position.z + normDirZ * speed * deltaTime;
            
            // Simple height check for climbable terrain
            if (window.terrainCollision) {
                const currentHeight = window.terrainCollision.getCollisionHeight(
                    zombie.position.x, zombie.position.z);
                const targetHeight = window.terrainCollision.getCollisionHeight(newX, newZ);
                
                // Maximum height zombies can climb (1 block)
                const maxClimbHeight = 1.0;
                
                // Calculate height difference
                const heightDiff = targetHeight - currentHeight;
                
                // If the height difference is within climbing range, allow movement
                if (heightDiff <= maxClimbHeight) {
                    // Calculate the actual distance including vertical movement
                    const horizontalDist = Math.sqrt((newX - zombie.position.x) ** 2 + (newZ - zombie.position.z) ** 2);
                    const totalDist = Math.sqrt(horizontalDist ** 2 + heightDiff ** 2);
                    
                    // Adjust speed to maintain consistent total velocity
                    const speedMultiplier = horizontalDist / totalDist;
                    
                    // Apply adjusted movement
                    zombie.position.x = zombie.position.x + (normDirX * speed * speedMultiplier);
                    zombie.position.z = zombie.position.z + (normDirZ * speed * speedMultiplier);
                    
                    // Smoothly interpolate height
                    zombie.position.y = targetHeight + 1.0;
                }
            } else {
                // Fallback if no collision manager
                const targetHeight = window.chunkManager.getHeightAt(newX, newZ) + 1;
                zombie.position.set(newX, targetHeight, newZ);
            }
            
            // Make zombie face player
            zombie.lookAt(playerCube.position);
            
            // Update zombie animation if it has an update method
            if (zombie.update) {
                zombie.update(1/60); // Approximately one frame at 60fps
            }
            
            // Update hitbox position for future collision detection
            if (zombie.hitbox) {
                zombie.hitbox.setFromObject(zombie);
            }
        });
    }

    // Create floating XP text at position
    createXpText(position, amount) {
        // Convert world position to screen position
        const tempV = new THREE.Vector3();
        tempV.copy(position);
        tempV.project(window.camera);
        
        const x = (tempV.x + 1) * window.innerWidth / 2;
        const y = (-tempV.y + 1) * window.innerHeight / 2;

        // Create XP text element
        const xpText = document.createElement('div');
        xpText.textContent = `+${amount} XP`;
        xpText.style.cssText = `
            position: fixed;
            left: ${x}px;
            top: ${y}px;
            color: #ffcc00;
            font-size: 18px;
            font-weight: bold;
            font-family: Arial;
            text-shadow: 1px 1px 0 #000;
            pointer-events: none;
            user-select: none;
            z-index: 10000;
            transform: translate(-50%, -50%);
        `;

        document.body.appendChild(xpText);

        // Animate
        let startTime = performance.now();
        let duration = 1500;

        function animate() {
            const elapsed = performance.now() - startTime;
            const progress = elapsed / duration;

            if (progress >= 1) {
                xpText.remove();
                return;
            }

            xpText.style.transform = `translate(-50%, ${-50 - progress * 70}px)`;
            xpText.style.opacity = 1 - progress;

            requestAnimationFrame(animate);
        }

        requestAnimationFrame(animate);
    }

    /**
     * Spawn a wave of zombies based on wave number
     * @param {number} waveNumber - Current wave number
     * @param {Object} zombieCounts - Object with counts for each zombie type
     */
    spawnWave(waveNumber, zombieCounts) {
        // Cancel any existing spawn interval
        if (this.spawnInterval) {
            clearInterval(this.spawnInterval);
        }
        
        // Get total zombies from WaveManager (includes spawn multiplier)
        const totalZombies = window.waveManager?.totalZombiesInWave || 
            (zombieCounts.base + zombieCounts.runners + zombieCounts.bosses);
        
        // Calculate the scaling factor to apply to zombie counts
        const originalTotal = zombieCounts.base + zombieCounts.runners + zombieCounts.bosses;
        const scalingFactor = totalZombies / originalTotal;
        
        // Scale the zombie counts proportionally, but respect maxZombies limit
        const scaledBase = Math.ceil(zombieCounts.base * scalingFactor);
        const scaledRunners = Math.ceil(zombieCounts.runners * scalingFactor);
        const scaledBosses = Math.ceil(zombieCounts.bosses * scalingFactor);
        
        // Adjust counts to respect maxZombies limit
        const totalScaled = scaledBase + scaledRunners + scaledBosses;
        if (totalScaled > this.maxZombies) {
            const ratio = this.maxZombies / totalScaled;
            this.currentWaveZombies = {
                base: Math.ceil(scaledBase * ratio),
                runners: Math.ceil(scaledRunners * ratio),
                bosses: Math.ceil(scaledBosses * ratio)
            };
        } else {
            this.currentWaveZombies = {
                base: scaledBase,
                runners: scaledRunners,
                bosses: scaledBosses
            };
        }
        
        // Store wave configuration
        this.waveInProgress = true;
        
        // Scale zombie attributes based on wave (make them progressively stronger)
        this.scaleZombieAttributesForWave(waveNumber);
        
        console.log(`[ZombieManager] Spawning Wave ${waveNumber} with ${totalZombies} total zombies`);
        console.log(`[ZombieManager] Scaled counts (max ${this.maxZombies}) - Base: ${this.currentWaveZombies.base}, Runners: ${this.currentWaveZombies.runners}, Bosses: ${this.currentWaveZombies.bosses}`);
        
        // Start spawning zombies with interval for pacing
        this.startWaveSpawning();
    }
    
    /**
     * Start spawning zombies at intervals for the current wave
     */
    startWaveSpawning() {
        // Get reference to player for spawn positions
        const playerMesh = window.playerMesh || window.playerController?.mesh;
        if (!playerMesh) {
            console.error('[ZombieManager] Cannot spawn wave: player mesh not found');
            return;
        }
        
        // Calculate total zombies to spawn
        const totalZombies = this.currentWaveZombies.base + 
                           this.currentWaveZombies.runners + 
                           this.currentWaveZombies.bosses;
        
        // Faster spawning for larger waves, but with a minimum delay
        this.spawnDelay = Math.max(300, 1000 - (totalZombies * 10));
        
        // Start the spawn interval
        this.spawnInterval = setInterval(() => {
            // Check if we still have zombies to spawn
            if (this.currentWaveZombies.base <= 0 && 
                this.currentWaveZombies.runners <= 0 && 
                this.currentWaveZombies.bosses <= 0) {
                // All zombies have been queued for spawn, clear interval
                clearInterval(this.spawnInterval);
                this.spawnInterval = null;
                return;
            }
            
            // Check if we can spawn more zombies
            if (this.zombies.length >= this.maxZombies) {
                return; // At zombie limit, wait for next interval
            }
            
            // Special case for Wave 5: Spawn boss first if available
            if (window.waveManager?.getWave() === 5 && this.currentWaveZombies.bosses > 0 && this.zombiesSpawned === 0) {
                this.currentWaveZombies.bosses--;
                this.spawnSpecificZombie('boss', playerMesh);
                return;
            }
            
            // Special case for Wave 2: Spawn runner first if available
            if (window.waveManager?.getWave() === 2 && this.currentWaveZombies.runners > 0 && this.zombiesSpawned === 0) {
                this.currentWaveZombies.runners--;
                this.spawnSpecificZombie('runner', playerMesh);
                return;
            }
            
            // Determine which type of zombie to spawn next
            let zombieType = 'base';
            
            // Prioritize boss zombies for spawning
            if (this.currentWaveZombies.bosses > 0) {
                zombieType = 'boss';
                this.currentWaveZombies.bosses--;
            } 
            // Then prioritize runners after bosses
            else if (this.currentWaveZombies.runners > 0) {
                zombieType = 'runner';
                this.currentWaveZombies.runners--;
            }
            // Finally spawn base zombies
            else if (this.currentWaveZombies.base > 0) {
                zombieType = 'base';
                this.currentWaveZombies.base--;
            }
            
            // Spawn the selected zombie type
            this.spawnSpecificZombie(zombieType, playerMesh);
            
        }, this.spawnDelay);
    }
    
    /**
     * Spawn a specific type of zombie
     * @param {string} type - Type of zombie to spawn (base, runner, boss)
     * @param {Object} playerMesh - Reference to player for position
     */
    spawnSpecificZombie(type, playerMesh) {
        // Generate position around player
        const angle = Math.random() * Math.PI * 2;
        const distance = 15 + Math.random() * 5; // 15-20 units away from player
        const spawnX = playerMesh.position.x + Math.cos(angle) * distance;
        const spawnZ = playerMesh.position.z + Math.sin(angle) * distance;
        const groundHeight = window.chunkManager.getHeightAt(spawnX, spawnZ);
        const spawnY = groundHeight + 1;
        
        // Create appropriate zombie type
        let zombie;
        
        switch(type) {
            case 'boss':
                zombie = this.createBossZombie(spawnX, spawnY, spawnZ);
                break;
            case 'runner':
                zombie = this.createRunnerZombie(spawnX, spawnY, spawnZ);
                break;
            default:
                zombie = this.createRegularZombie(spawnX, spawnY, spawnZ);
                break;
        }
        
        // Add to tracking arrays
        this.zombies.push(zombie);
        this.scene.add(zombie);
        
        // Increment spawn counter
        this.zombiesSpawned++;
        
        if (DEBUG) {
            console.log(`Wave ${type} zombie spawned at: ${spawnX.toFixed(2)}, ${spawnY.toFixed(2)}, ${spawnZ.toFixed(2)}`);
        }
    }
    
    /**
     * Scale zombie attributes based on wave number
     * @param {number} waveNumber - Current wave number 
     */
    scaleZombieAttributesForWave(waveNumber) {
        // Calculate scaling factor (exponential difficulty)
        const scalingFactor = 1 + (waveNumber - 1) * 0.042;
        
        // Scale health
        this.startingHealth = Math.ceil(75 * scalingFactor);  
        this.runnerZombieHealth = Math.ceil(100 * scalingFactor);
        this.bossZombieHealth = Math.ceil(250 * scalingFactor);
        
        // Scale damage
        this.zombieDamage.regular = Math.ceil(10 * scalingFactor);
        this.zombieDamage.runner = Math.ceil(8 * scalingFactor);
        this.zombieDamage.boss = Math.ceil(25 * scalingFactor);
        
        // Scale movement speed (4.20% increase per wave)
        const speedScalingFactor = 1 + (waveNumber - 1) * 0.042;
        this.zombieSpeed = {
            regular: 0.048 * speedScalingFactor,    // Base speed
            runner: 0.072 * speedScalingFactor,     // Base speed
            boss: 0.034 * speedScalingFactor        // Base speed
        };
        
        // Faster attacks in later waves
        const attackSpeedFactor = Math.max(0.6, 1 - (waveNumber - 1) * 0.02);
        this.zombieAttackCooldown.regular = Math.ceil(1500 * attackSpeedFactor);
        this.zombieAttackCooldown.runner = Math.ceil(1000 * attackSpeedFactor);
        this.zombieAttackCooldown.boss = Math.ceil(2000 * attackSpeedFactor);
        
        console.log(`[ZombieManager] Wave ${waveNumber} difficulty scaling: x${scalingFactor.toFixed(2)}`);
        console.log(`[ZombieManager] Base zombie health: ${this.startingHealth}, damage: ${this.zombieDamage.regular}`);
        console.log(`[ZombieManager] Zombie speeds: Regular=${this.zombieSpeed.regular.toFixed(4)}, Runner=${this.zombieSpeed.runner.toFixed(4)}, Boss=${this.zombieSpeed.boss.toFixed(4)}`);
    }
}